import router from 'next/router';
import {t} from '../common/getTranslations';
import {requestContext} from '../common/requestContext';
import {getPagewebUrl as _getPagewebUrl} from '../common/shared/scheme/getPagewebUrl.function';
import {MalformedAppSchemeError} from '../common/shared/scheme/MalformedAppSchemeError.class';
import {PagewebUrl} from '../common/shared/scheme/PagewebUrl.class';
import {IS_CLIENT_SIDE} from '../common/shared/utils/constants';
import {showSimpleDialog} from '../dialog/simpleDialogHandler';
import {trackEvent as internalTrackEvent} from '../logging/trackEvent';
import {ShareType} from '../share/constants';
import {share as _share} from '../share/share';
import {ShareProps} from '../share/shareProps.interface';
import {AppStore} from '../state/store';
import {handleLoginPageweb} from '../user/handleLoginPageweb.function';
import {gotoUrlOptions} from './KakaoPageParentBridge.interface';

export function initKpParent(store: AppStore) {
  if (!IS_CLIENT_SIDE || window.KPParent) {
    return;
  }

  // V3 이벤트 페이지에서 스킴 실행을 위해 사용합니다.
  function gotoUrl(
    scheme: string,
    openNewWindow = false,
    options: gotoUrlOptions = {
      width: 460,
      height: 700,
    },
    handleError = true
  ) {
    function openWindow(url: string) {
      const {width, height, fullScreen, newTab} = options;

      if (window.top) {
        const popupFeatures = `toolbar=no, directories=no, scrollbars=yes, resizable=no, status=no, menubar=no, location=no, width=${
          fullScreen ? screen.width : width
        }, height=${fullScreen ? screen.height : height}`;
        const popup = window.top.open(
          url,
          newTab ? '_blank' : url,
          newTab ? undefined : popupFeatures
        );
        if (!popup) {
          const title = t('alert_message_popup_blocked');
          const desc = t('alert_message_turn_off_popup_blocker');
          window.top.alert(`${title}\n\n${desc}`);
        }
      }
    }
    try {
      if (scheme) {
        const _url = _getPagewebUrl({
          appScheme: scheme,
        });
        if (_url instanceof PagewebUrl) {
          if (_url.openNewWindow || openNewWindow) {
            openWindow(_url.fullUrl);
          } else {
            if (window.top) {
              router.push(_url.fullUrl);
            }
          }
          return;
        } else {
          if (window.top) {
            if (openNewWindow) {
              openWindow(_url);
            } else {
              window.top.location.href = _url;
            }
          }
          return;
        }
      } else {
        throw new MalformedAppSchemeError();
      }
    } catch {
      if (handleError) {
        showSimpleDialog({
          title: t('webview_unsupported_function_not_anymore'),
          isMobile: requestContext.requestInfo.display.isMobile,
          okButton: {
            text: t('common_confirm_button'),
            type: 'label',
            closeAfterClick: true,
          },
        });
      }
    }
  }

  // V3 이벤트 페이지에서 로그인 여부 확인을 위해 사용합니다.
  function isLogin() {
    const user = store.getState().user.user;
    return !!user;
  }

  function login() {
    handleLoginPageweb();
  }

  function trackEvent(eventObjectJsonString: string | object): void {
    internalTrackEvent(
      typeof eventObjectJsonString === 'string'
        ? JSON.parse(eventObjectJsonString)
        : eventObjectJsonString
    );
  }
  function share(props: ShareProps): void {
    // 카카오톡 공유의 경우 비로그인 상태에서 공유 시도 시 카카오톡 로그인 창이 뜨지만,
    // 해당 창에서 로그인을 해도 페이지웹 로그인은 되지 않는 문제가 발생합니다.
    // 따라서, 카카오톡 공유이며 페이지웹 로그인이 되지 않은 경우 먼저 페이지웹 로그인을 시키도록 강제합니다.
    if (
      props.shareType === ShareType.KakaoTalk &&
      !store.getState().user.user
    ) {
      handleLoginPageweb();
    } else {
      _share(props);
    }
  }

  function showDialog({
    title,
    message,
    confirm,
    cancel,
  }: {
    title: string;
    message?: string;
    confirm?: {text: string; action: string};
    cancel?: {text: string; action: string};
  }) {
    // cancel 및 confirm 버튼의 텍스트가 유효한 경우에만 노출합니다.
    const showCancelButton = !!cancel?.text;
    const showConfirmButton = !!confirm?.text;

    showSimpleDialog({
      title,
      description: message,
      isMobile: requestContext.requestInfo.display.isMobile,
      // cancel 버튼이 존재할 때만 dimmed 영역 클릭 시 다이얼로그를 닫습니다.
      shouldCloseOnDimmedClick: showCancelButton,
      ...(showConfirmButton && {
        okButton: {
          text: confirm.text,
          type: 'label',
          closeAfterClick: true,
          onClick: () =>
            gotoUrl(
              confirm.action ?? '',
              false,
              {width: 460, height: 700},
              false
            ),
        },
      }),
      ...(showCancelButton && {
        cancelButton: {
          text: cancel.text,
          type: 'label',
          onClick: () =>
            gotoUrl(
              cancel.action ?? '',
              false,
              {width: 460, height: 700},
              false
            ),
        },
        onClose: () =>
          gotoUrl(cancel.action ?? '', false, {width: 460, height: 700}, false),
      }),
    });
  }

  window.KPParent = {
    gotoUrl,
    isLogin,
    login,
    trackEvent,
    share,
    showDialog,
  };
}
